import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import FilterPanel from "./components/FilterPanel";
import { categoryMeta } from "@/mocks/videos";
import { defaultFilters, filterAndSort, type FilterOptions } from "@/utils/video";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;
const validTypes = ["movie", "tv", "variety"];

interface CategoryProps {
  mode?: "list" | "filter";
}

export default function Category({ mode = "list" }: CategoryProps) {
  const params = useParams<{ type: string }>();
  const navigate = useNavigate();
  const type = validTypes.includes(params.type || "") ? (params.type as string) : "movie";

  const [filters, setFilters] = useState<FilterOptions>({ ...defaultFilters });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const meta = categoryMeta.find((c) => c.type === type)!;
  const isFilterMode = mode === "filter";

  useDocumentMeta({
    title: `${meta.label}列表 - 在线观看_免费视频播放平台`,
    description: `在线播放提供全面${meta.label}资源，支持筛选和分类浏览，轻松找到喜欢影片。`,
  });

  useEffect(() => {
    setFilters({ ...defaultFilters });
    setVisible(PAGE_SIZE);
  }, [type]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters]);

  const results = useMemo(() => filterAndSort(type, filters), [type, filters]);
  const shown = results.slice(0, visible);
  const hasMore = visible < results.length;

  const updateFilters = (next: FilterOptions) => setFilters(next);
  const resetFilters = () => setFilters({ ...defaultFilters });
  const handleTypeChange = (nextType: string) => {
    navigate(`/category/${nextType}${isFilterMode ? "/filter" : ""}`);
  };

  return (
    <div className="pt-16 md:pt-20">
      <div className="px-4 md:px-8 pt-6 md:pt-8">
        {/* Breadcrumb */}
        <nav className="flex items-center flex-wrap gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${type}`} className="hover:text-accent-500 transition-colors cursor-pointer">
            {meta.label}
          </Link>
          {isFilterMode && (
            <>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700">筛选结果</span>
            </>
          )}
        </nav>

        {/* Header */}
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 flex items-center gap-3">
              <i className={`${meta.icon} text-accent-500`} />
              {meta.label}频道
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 leading-relaxed">{meta.desc}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-background-200/70 text-foreground-950 cursor-pointer"
            >
              <i className="ri-filter-3-line" /> 筛选
            </button>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/70">
              <button
                type="button"
                onClick={() => setFilters({ ...filters, sort: "latest" })}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  filters.sort === "latest" ? "bg-accent-500 text-background-50 font-medium" : "text-foreground-700"
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => setFilters({ ...filters, sort: "hot" })}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  filters.sort === "hot" ? "bg-accent-500 text-background-50 font-medium" : "text-foreground-700"
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter panel (desktop) */}
      <div className="px-4 md:px-8 mt-6">
        <div className="hidden lg:block">
          <FilterPanel
            activeType={type}
            onTypeChange={handleTypeChange}
            filters={filters}
            onChange={updateFilters}
            onReset={resetFilters}
          />
        </div>
      </div>

      {/* Results */}
      <div className="px-4 md:px-8 py-8 md:py-10">
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-600">
            共找到 <span className="text-accent-500 font-semibold">{results.length}</span> 部{meta.label}
          </p>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {shown.map((video, i) => (
                <VideoCard key={video.id} video={video} index={i} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8 md:mt-10">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-lg" /> 加载更多（剩余 {results.length - visible} 部）
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-300/50">
            <span className="w-16 h-16 rounded-full bg-background-200/70 flex items-center justify-center">
              <i className="ri-search-eye-line text-3xl text-foreground-500" />
            </span>
            <p className="mt-4 text-foreground-800 font-medium">暂无符合条件的视频</p>
            <p className="mt-1 text-sm text-foreground-500">换个筛选条件试试看</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" /> 重置筛选
            </button>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-background-50/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-100 p-4 animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-semibold text-foreground-950">筛选</span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200/70 text-foreground-950 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel
              activeType={type}
              onTypeChange={handleTypeChange}
              filters={filters}
              onChange={updateFilters}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full py-3 rounded-md font-medium bg-primary-500 text-background-50 cursor-pointer"
            >
              查看 {results.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}